"use client";
import { useBreakpoints } from "@/hooks/useBreakpoints/useBreakpoints";
import { cn } from "@/lib/utils";
import { useGSAP } from "@gsap/react";
import { gsap } from "gsap";
import { useRef } from "react";
import { MaskPropsModel } from "./Mask.model";

const Mask = ({ className = "" }: MaskPropsModel) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { isMobile } = useBreakpoints();

  useGSAP(() => {
    // Function to animate SVG paths
    const animateSVGPaths = (paths: NodeListOf<SVGPathElement>) => {
      if (paths.length >= 4) {
        // Reset positions (from edges)
        gsap.set(paths[0], { x: -300, opacity: 0 }); // Left path
        gsap.set(paths[1], { y: -300, opacity: 0 }); // Top path
        gsap.set(paths[2], { y: 300, opacity: 0 }); // Bottom path
        gsap.set(paths[3], { x: 300, opacity: 0 }); // Right path

        // Animate to center
        gsap.to(paths, {
          x: 0,
          y: 0,
          opacity: 1,
          duration: 1.8,
          ease: "power2.out",
          stagger: 0.25,
          delay: 0.3, // Small delay before animation starts
        });
      }
    };

    // Function to find SVG and paths
    const findAndAnimateSVGPaths = () => {
      // Try different strategies to find the SVG with id 'mask'
      let svgElement = document.querySelector("svg#mask");

      if (!svgElement) {
        // Try to find SVG inside the maskImage
        const maskImage = document.getElementById("maskImage");
        if (maskImage) {
          svgElement = maskImage.querySelector("svg");
        }
      }

      if (!svgElement) {
        // Try to find any SVG in the container
        svgElement = containerRef.current?.querySelector("svg") || null;
      }

      if (svgElement) {
        // Find all path elements inside the SVG
        const paths = svgElement.querySelectorAll("path");
        if (paths.length >= 4) {
          animateSVGPaths(paths);
          return true;
        } else {
          console.error("SVG found but not enough paths:", paths.length);
        }
      }

      return false;
    };

    // Try immediately
    if (!findAndAnimateSVGPaths()) {
      // If not successful, set up a MutationObserver to watch for the SVG being added to the DOM
      const observer = new MutationObserver(mutations => {
        for (const mutation of mutations) {
          if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
            // Check if our SVG has been added
            if (findAndAnimateSVGPaths()) {
              // If we found and animated the paths, disconnect the observer
              observer.disconnect();
              break;
            }
          }
        }
      });

      // Start observing the container for changes
      if (containerRef.current) {
        observer.observe(containerRef.current, {
          childList: true,
          subtree: true,
        });

        // Also set a timeout as a fallback
        const timer = setTimeout(() => {
          findAndAnimateSVGPaths();
          observer.disconnect();
        }, 1500);

        return () => {
          clearTimeout(timer);
          observer.disconnect();
        };
      }
    }
  }, []);

  return (
    <div className={cn("h-full w-full", className)} ref={containerRef}>
      {!isMobile ? (
        <svg
          className="mask-svg absolute top-0 left-0 h-full w-full"
          width="1440"
          height="750"
          viewBox="0 0 1440 750"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
        >
          <defs>
            <mask id="mask">
              <rect
                width="1440"
                height="750"
                fill="white"
                shapeRendering="crispEdges"
              ></rect>
              <path
                d="M391.808 194.556H611.619C618.204 194.556 623.552 189.212 623.552 182.632V27.9243C623.552 21.3447 618.204 16 611.619 16H16V734H611.619C618.204 734 623.552 728.655 623.552 722.076V213.62C623.552 207.04 618.204 201.695 611.619 201.695H391.808C389.838 201.695 388.236 200.094 388.236 198.126C388.236 196.158 389.838 194.556 391.808 194.556ZM326.911 293.308H291.209V270.617H326.911C333.186 270.617 338.264 275.692 338.264 281.963C338.264 288.234 333.186 293.308 326.911 293.308ZM338.264 144.1C338.264 150.371 333.186 155.445 326.911 155.445H291.209V132.754H326.911C333.186 132.754 338.264 137.829 338.264 144.1Z"
                fill="black"
                className="b"
                data-svg-origin="16 16"
                transform="matrix(1,0,0,1,0,0)"
              ></path>
              <path
                d="M809.38 105.278C809.38 55.9712 769.38 16 720.038 16C670.696 16 630.696 55.9712 630.696 105.278C630.696 154.585 670.696 194.556 720.038 194.556C769.38 194.556 809.38 154.585 809.38 105.278Z"
                fill="black"
                className="dot-i"
                data-svg-origin="630.6959838867188 16"
                transform="matrix(1,0,0,1,0,0)"
              ></path>
              <path
                d="M797.447 201.695H642.629C636.039 201.695 630.696 207.034 630.696 213.62V722.076C630.696 728.661 636.039 734 642.629 734H797.447C804.038 734 809.38 728.661 809.38 722.076V213.62C809.38 207.034 804.038 201.695 797.447 201.695Z"
                fill="black"
                className="i"
                data-svg-origin="630.6959838867188 201.69500732421875"
                transform="matrix(1,0,0,1,0,0)"
              ></path>
              <path
                d="M1423.98 182.632V16H828.458C821.874 16 816.525 21.3447 816.525 27.9243V722.076C816.525 728.655 821.874 734 828.458 734H1423.98V213.62C1423.98 207.04 1418.63 201.695 1412.05 201.695H1121.32V270.617H1132.67V281.635C1132.67 287.751 1127.98 293 1121.88 293.289C1115.35 293.598 1109.98 288.407 1109.98 281.963V144.428C1109.98 138.311 1114.67 133.063 1120.77 132.774C1127.3 132.465 1132.67 137.655 1132.67 144.1V194.556H1412.07C1418.65 194.556 1424 189.212 1424 182.632H1423.98Z"
                fill="black"
                className="g"
                data-svg-origin="816.5250244140625 16"
                transform="matrix(1,0,0,1,0,0)"
              ></path>
            </mask>
          </defs>
          <rect
            className="mask-overlay"
            width="1440"
            height="750"
            fill="red"
            shapeRendering="crispEdges"
            mask="url(#mask)"
          ></rect>
        </svg>
      ) : (
        <svg
          className="mask-svg-mobile absolute top-0 left-0 h-full w-full border-8 border-[#f00]"
          xmlns="http://www.w3.org/2000/svg"
          width="359"
          height="526"
          viewBox="0 0 359 526"
          fill="none"
          preserveAspectRatio="none"
        >
          <defs>
            <mask id="mask-mobile">
              <rect width="359" height="526" fill="white"></rect>
              <path
                d="M88.503 86.4832H141.899C145.024 86.4832 147.557 83.95 147.557 80.8255V5.65767C147.557 2.53312 145.024 0 141.899 0H0V526H141.888C145.013 526 147.546 523.467 147.546 520.342V95.522C147.546 92.3975 145.013 89.8644 141.888 89.8644H88.4918C87.5545 89.8644 86.7956 89.1056 86.7956 88.1682C86.7956 87.2308 87.5545 86.472 88.4918 86.472L88.503 86.4832ZM77.6563 133.318H60.7391V122.561H77.6563C80.6246 122.561 83.035 124.971 83.035 127.939C83.035 130.908 80.6246 133.318 77.6563 133.318ZM83.035 62.558C83.035 65.5263 80.6246 67.9367 77.6563 67.9367H60.7391V57.1793H77.6563C80.6246 57.1793 83.035 59.5897 83.035 62.558Z"
                fill="black"
                className="b"
                data-svg-origin="8 8"
              ></path>
              <path
                d="M208.106 28.5841C208.106 12.7975 195.309 0 179.522 0C163.736 0 150.938 12.7975 150.938 28.5841C150.938 44.3706 163.736 57.1682 179.522 57.1682C195.309 57.1682 208.106 44.3706 208.106 28.5841Z"
                fill="black"
                className="dot-i"
                data-svg-origin="8 8"
              ></path>
              <path
                d="M202.448 60.5605H156.596C153.471 60.5605 150.938 63.0936 150.938 66.2182V520.342C150.938 523.467 153.471 526 156.596 526H202.448C205.573 526 208.106 523.467 208.106 520.342V66.2182C208.106 63.0936 205.573 60.5605 202.448 60.5605Z"
                fill="black"
                className="i"
                data-svg-origin="8 8"
              ></path>
              <path
                d="M359 80.8255V0H217.156C214.032 0 211.499 2.53312 211.499 5.65767V520.342C211.499 523.467 214.032 526 217.156 526H359V95.522C359 92.3975 356.467 89.8644 353.342 89.8644H283.252V122.549H288.63V127.772C288.63 130.673 286.41 133.162 283.508 133.296C280.417 133.441 277.873 130.975 277.873 127.917V62.6808C277.873 59.7794 280.094 57.2909 282.995 57.157C286.086 57.0119 288.63 59.4781 288.63 62.5357V86.4609H353.331C356.455 86.4609 358.989 83.9277 358.989 80.8032L359 80.8255Z"
                fill="black"
                className="g"
                data-svg-origin="8 8"
              ></path>
            </mask>
          </defs>
          <rect
            className="mask-overlay"
            width="359"
            height="526"
            fill="red"
            mask="url(#mask-mobile)"
          ></rect>
        </svg>
      )}
    </div>
  );
};

export default Mask;
